import type { PaletteMode, Theme } from '@mui/material';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DEFAULT_PARTY_BACKGROUND, isPartyColor } from './partyPalette';
import { createSiTheme } from './theme';
import { ThemeModeContext } from './ThemeModeContext';

interface Props {
  children: React.ReactNode;
  theme?: Theme;
  userId?: number;
}

const PARTY_ENABLED_KEY = 'ics-ui:party-theme:880:enabled';
const PARTY_BACKGROUND_KEY = 'ics-ui:party-theme:880:background';

const LOCAL_STORAGE_THEME_KEY = 'ics-ui:theme-mode';

function getInitialMode(): PaletteMode {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = window.localStorage.getItem(LOCAL_STORAGE_THEME_KEY);
  return stored === 'dark' ? 'dark' : 'light';
}

export function ThemeProvider({ children, theme, userId }: Props) {
  const [mode, setMode] = useState<PaletteMode>(() => getInitialMode());

  const partyAvailable = userId === 880;
  const [partyRequested, setPartyRequested] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(PARTY_ENABLED_KEY) === 'true'
  );
  const [partyBackground, setBackground] = useState(() => {
    const stored =
      typeof window !== 'undefined' ? window.localStorage.getItem(PARTY_BACKGROUND_KEY) : null;
    return stored && isPartyColor(stored) ? stored : DEFAULT_PARTY_BACKGROUND;
  });
  const partyEnabled = partyAvailable && partyRequested;
  const setPartyEnabled = useCallback(
    (enabled: boolean) => {
      if (!partyAvailable) return;
      setPartyRequested(enabled);
      window.localStorage.setItem(PARTY_ENABLED_KEY, String(enabled));
    },
    [partyAvailable]
  );
  const setPartyBackground = useCallback(
    (color: string) => {
      if (!partyAvailable || !isPartyColor(color)) return;
      setBackground(color);
      window.localStorage.setItem(PARTY_BACKGROUND_KEY, color);
    },
    [partyAvailable]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LOCAL_STORAGE_THEME_KEY, mode);
    }
  }, [mode]);

  const setColorMode = useCallback(
    (nextMode: PaletteMode) => {
      setMode(nextMode === 'dark' ? 'dark' : 'light');
      setPartyEnabled(false);
    },
    [setPartyEnabled]
  );

  const toggleMode = useCallback(() => {
    setMode(partyEnabled ? 'light' : mode === 'dark' ? 'light' : 'dark');
    setPartyEnabled(false);
  }, [mode, partyEnabled, setPartyEnabled]);

  const contextValue = useMemo(
    () => ({
      mode: partyEnabled ? ('dark' as const) : mode,
      partyAvailable,
      partyEnabled,
      partyBackground,
      setPartyEnabled,
      setPartyBackground,
      setMode: setColorMode,
      toggleMode,
    }),
    [
      mode,
      partyAvailable,
      partyEnabled,
      partyBackground,
      setPartyEnabled,
      setPartyBackground,
      setColorMode,
      toggleMode,
    ]
  );

  const resolvedTheme = useMemo(
    () => theme ?? createSiTheme(mode, partyEnabled ? partyBackground : undefined),
    [mode, theme, partyEnabled, partyBackground]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={resolvedTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
